import { Axios, AxiosError, AxiosResponse } from 'axios'
import { RemoteConfig } from './remote.config'

export class ReportData {
	id: string;
	created_at: Date;
	source: string;
	type: string;

	values: any;

	constructor(id: string, created_at: Date, source: string, type: string, values: any) {
		this.id = id;
		this.created_at = created_at;
		this.source = source;
		this.type = type;
		this.values = values;
	}
}

export type ReportsForSourceCallback = (data?: ReportData[], resp?: AxiosResponse, err?: AxiosError) => void;

export const getReportsForSource = (source: string, callback: ReportsForSourceCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/supplier/${source}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: ReportData[] = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type LatestReportForSourceCallback = (data?: ReportData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getLatestReportForSource = (source: string, callback: LatestReportForSourceCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/supplier/${source}/latest`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: ReportData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export class ReportTimeseriesData {
	created_at: Date;
	value: string;

	constructor(created_at: Date, value: string) {
		this.created_at = created_at;
		this.value = value;
	}
}

export type TimeseriesForSupplierCallback = (data?: ReportTimeseriesData[], resp?: AxiosResponse, err?: AxiosError) => void;
export const getTimeseriesForSupplier = (supplier: string, timeseries_id: string, callback: TimeseriesForSupplierCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/supplier/${supplier}/timeseries/${timeseries_id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			const dateTimeReviver = (key, value) => {
				if (key == "created_at") {
					return new Date(value);
				}
				return value;
			}

			let data: ReportTimeseriesData[] = JSON.parse(resp.data, dateTimeReviver)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}


export class AIReportData {
	id: string;
	source: string;
	report_type: string;
	requested_at: Date;
	started_at: Date | null;
	finished_at: Date | null;
	report_markdown: string;
	new_report_possible: boolean;

	constructor(id: string, source: string, report_type: string, requested_at: Date, started_at: Date | null, finished_at: Date | null, report_markdown: string, new_report_possible: boolean) {
		this.id=id;
		this.source=source;
		this.report_type=report_type;
		this.requested_at=requested_at;
		this.started_at=started_at;
		this.finished_at=finished_at;
		this.report_markdown=report_markdown;
		this.new_report_possible=new_report_possible;
	}
}

const dateTimeReviver = (key, value) => {
	if (key == "created_at" || key == "requested_at" || key == "finished_at" || key == "started_at") {
		if (value != null && value != undefined) {
			return new Date(value);
		} else {
			return value;
		}
	}
	return value;
}

const parseAIReport = (data: string) => {
	try {
		let result: AIReportData = JSON.parse(data, dateTimeReviver)
		return result
	} catch {
		return undefined
	}
}

const parseAIReports = (data: string) => {
	try {
		let result: AIReportData[] = JSON.parse(data, dateTimeReviver)
		return result
	} catch {
		return undefined
	}
}

export type AIReportCallback = (data?: AIReportData, resp?: AxiosResponse, err?: AxiosError) => void;
export const getAIReport = (report: string, callback: AIReportCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/report/${report}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData | undefined = parseAIReport(resp.data)
			callback(data, resp)
		}
	})
}

export type AIReportsForHostCallback = (data?: AIReportData[], resp?: AxiosResponse, err?: AxiosError) => void;
export const getAIReportsForHost = (source: string, callback: AIReportsForHostCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/host/${source}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData[] | undefined = parseAIReports(resp.data)
			callback(data, resp)
		}
	})
}

export type LatestAIReportForHostCallback = (data?: AIReportData, resp?: AxiosResponse, err?: AxiosError) => void;
export const getLatestAIReportForHost = (source: string, callback: LatestAIReportForHostCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/host/${source}/latest`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData | undefined = parseAIReport(resp.data)
			callback(data, resp)
		}
	})
}

export type RequestAIReportForHostCallback = (data?: AIReportData, resp?: AxiosResponse, err?: AxiosError) => void;
export const requestAIReportForHost = (source: string, callback: RequestAIReportForHostCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/host/${source}`, RemoteConfig.url);
	const result = client.post(url.toString(), null, {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData | undefined = parseAIReport(resp.data)
			callback(data, resp)
		}
	})
}

export type AIReportsForSupplierCallback = (data?: AIReportData[], resp?: AxiosResponse, err?: AxiosError) => void;
export const getAIReportsForSupplier = (source: string, callback: AIReportsForSupplierCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/supplier/${source}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData[] | undefined = parseAIReports(resp.data)
			callback(data, resp)
		}
	})
}

export type LatestAIReportForSupplierCallback = (data?: AIReportData, resp?: AxiosResponse, err?: AxiosError) => void;
export const getLatestAIReportForSupplier = (source: string, callback: LatestAIReportForSupplierCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/supplier/${source}/latest`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData | undefined = parseAIReport(resp.data)
			callback(data, resp)
		}
	})
}

export type RequestAIReportForSupplierCallback = (data?: AIReportData, resp?: AxiosResponse, err?: AxiosError) => void;
export const requestAIReportForSupplier = (source: string, callback: RequestAIReportForSupplierCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/reports/ai/supplier/${source}`, RemoteConfig.url);
	const result = client.post(url.toString(), null, {withCredentials:true}).then((resp) => {
		if (resp.status < 200 || resp.status >= 300) {
			callback(undefined, resp)
		} else {
			let data: AIReportData | undefined = parseAIReport(resp.data)
			callback(data, resp)
		}
	})
}

